/**
 * YearRoundStrategyHubPage - landing page that compares the four year-round
 * strategies side by side. Click any tile to open the full assessment.
 */
import { Link } from 'react-router';
import { CalendarClock, ArrowRight, ShieldCheck, TrendingDown, Activity, Layers } from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import { YEAR_ROUND_STRATEGIES } from '@/lib/yearRoundStrategies';

const VERDICT_BADGE: Record<string, string> = {
  recommended:     'bg-emerald-700/40 border-emerald-500 text-emerald-200',
  good:            'bg-cyan-700/40    border-cyan-500    text-cyan-200',
  acceptable:      'bg-amber-700/40   border-amber-500   text-amber-200',
  not_recommended: 'bg-rose-700/40    border-rose-500    text-rose-200',
};

const VERDICT_LABEL: Record<string, string> = {
  recommended:     'RECOMMENDED',
  good:            'GOOD',
  acceptable:      'ACCEPTABLE',
  not_recommended: 'NOT RECOMMENDED',
};

const PATH_BY_ID: Record<string, string> = {
  continuous_full:     '/scenarios/year-round/strategy-a-continuous',
  summer_full_mix:     '/scenarios/year-round/strategy-b-summer-fullmix',
  adaptive_scheduler:  '/scenarios/year-round/strategy-c-adaptive',
  tem_only_summer:     '/scenarios/year-round/strategy-d-tem-only',
};

export default function YearRoundStrategyHubPage() {
  return (
    <div className="space-y-4 p-4">
      <PageHeader icon={CalendarClock}
        title="Year-round operating strategies - 4 scenarios"
        subtitle="Four assessed strategies for Barcombe annual cycle. Click any tile for the full assessment with monthly regime, modules engaged, pros / cons and UK compliance."
      />

      {/* Summary comparison strip */}
      <div className="rounded-xl bg-slate-900/40 border border-slate-700 p-4">
        <h3 className="text-sm font-semibold text-cyan-300 mb-3 flex items-center gap-2">
          <Layers className="w-4 h-4" /> Quick comparison
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="text-slate-400 border-b border-slate-700">
              <tr>
                <th className="text-left  py-1.5 px-2">Strategy</th>
                <th className="text-center py-1.5 px-2">Verdict</th>
                <th className="text-right  py-1.5 px-2">Overall</th>
                <th className="text-right  py-1.5 px-2">Cyano</th>
                <th className="text-right  py-1.5 px-2">Hypo O2</th>
                <th className="text-right  py-1.5 px-2">Raw water</th>
                <th className="text-right  py-1.5 px-2">Compliance</th>
                <th className="text-right  py-1.5 px-2">MWh/yr</th>
                <th className="text-right  py-1.5 px-2">Modules</th>
              </tr>
            </thead>
            <tbody>
              {YEAR_ROUND_STRATEGIES.map(s => (
                <tr key={s.id} className="border-b border-slate-800/50">
                  <td className="py-1 px-2 text-slate-100 font-medium">{s.letter}. {s.label.split('· ')[1]}</td>
                  <td className="py-1 px-2 text-center">
                    <span className={`px-2 py-0.5 rounded text-[10px] font-bold border ${VERDICT_BADGE[s.verdict]}`}>
                      {VERDICT_LABEL[s.verdict]}
                    </span>
                  </td>
                  <td className="py-1 px-2 text-right text-slate-200 font-semibold">{s.health.overall}</td>
                  <td className="py-1 px-2 text-right text-slate-300">{s.health.cyanoControl}</td>
                  <td className="py-1 px-2 text-right text-slate-300">{s.health.hypolimnionO2}</td>
                  <td className="py-1 px-2 text-right text-slate-300">{s.health.rawWaterQuality}</td>
                  <td className="py-1 px-2 text-right text-slate-300">{s.health.complianceMargin}</td>
                  <td className="py-1 px-2 text-right text-slate-300">{s.annualEnergy_MWh.toFixed(1)}</td>
                  <td className="py-1 px-2 text-right text-slate-300">{s.modules.length}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Tile grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {YEAR_ROUND_STRATEGIES.map(s => (
          <Link key={s.id} to={PATH_BY_ID[s.id]}
            className="rounded-xl border-2 bg-slate-900/40 hover:bg-slate-800/60 border-slate-700 hover:border-cyan-700 p-4 transition-colors flex flex-col gap-3">
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="text-xs uppercase tracking-wider text-slate-400">Scenario {s.letter}</div>
                <h3 className="text-lg font-bold text-cyan-200 leading-tight">{s.label.split('· ')[1]}</h3>
                <p className="text-xs text-slate-400 mt-1">{s.tagline}</p>
              </div>
              <span className={`px-2 py-1 rounded text-[10px] font-bold border whitespace-nowrap ${VERDICT_BADGE[s.verdict]}`}>
                {VERDICT_LABEL[s.verdict]}
              </span>
            </div>

            <p className="text-xs text-slate-300 leading-relaxed line-clamp-4">{s.philosophy}</p>

            <div className="grid grid-cols-3 gap-2 text-xs">
              <Stat icon={<ShieldCheck className="w-3 h-3" />} label="Health" value={`${s.health.overall} / 100`} tone={s.health.overall >= 85 ? 'good' : s.health.overall >= 70 ? 'warn' : 'bad'} />
              <Stat icon={<TrendingDown className="w-3 h-3" />} label="Energy" value={`${s.annualEnergy_MWh.toFixed(1)} MWh/yr`} tone={s.annualEnergy_MWh < 100 ? 'good' : s.annualEnergy_MWh < 250 ? 'warn' : 'bad'} />
              <Stat icon={<Activity className="w-3 h-3" />} label="Modules" value={`${s.modules.length}`} />
            </div>

            <div className="flex items-center justify-between mt-auto pt-2 border-t border-slate-700/60">
              <span className="text-[11px] text-slate-400">Click for full year-round assessment</span>
              <ArrowRight className="w-4 h-4 text-cyan-300" />
            </div>
          </Link>
        ))}
      </div>

      {/* Research basis recap */}
      <div className="rounded-xl bg-slate-900/40 border border-slate-700 p-4 text-xs text-slate-300 leading-relaxed">
        <h3 className="text-sm font-semibold text-cyan-300 mb-2">Research basis (shared across all four)</h3>
        <p>
          All four strategies are benchmarked against the same Barcombe annual climatology (PSO-1 trajectory: T_surf 5 → 23 → 5 °C,
          ΔT 0.5 → 9 → 0.5 °C, LN 0.2 → 2.6 → 0.2, cyano fraction 0.05 → 0.60 → 0.05 across Jan-Dec) and the same global
          research lineage: Cooke, Welch, Peterson & Nichols 2005 (Restoration & Management of Lakes & Reservoirs, 3rd ed.,
          CRC); Antenucci, Imberger & Heinz 2000 (Lake Number); Beutel & Horne 1999 (seasonal hypolimnetic envelope);
          Gächter & Wehrli 1998 (Lake Sempach 27-year retrospective); Hipsey, Bruce et al. 2017 (GLM-AED2); Patterson,
          Hamblin & Imberger 1984 (TEM origin); Hamilton & Schladow 1997; Brookes & Carey 2011 (Science 334); Imberger 1985;
          Wilhelm & Adrian 2008 (winter cyanobacteria); Adrian et al. 2009 (winter limnology paradigm shift).
        </p>
      </div>
    </div>
  );
}

function Stat({ icon, label, value, tone = 'neutral' }: { icon: React.ReactNode; label: string; value: string; tone?: 'neutral' | 'good' | 'warn' | 'bad' }) {
  const cls = tone === 'good' ? 'text-emerald-300' : tone === 'warn' ? 'text-amber-300' : tone === 'bad' ? 'text-rose-300' : 'text-slate-200';
  return (
    <div className="rounded-md bg-slate-900/60 border border-slate-700 px-2 py-1.5">
      <div className="flex items-center gap-1 text-[10px] text-slate-500 uppercase tracking-wide">{icon}{label}</div>
      <div className={`text-sm font-semibold mt-0.5 ${cls}`}>{value}</div>
    </div>
  );
}
